import { QueryParams } from '@ofqual/portal-core/core/types';
import {
 API_HOST_COMMS,
 API_HOST_ORGS,
 API_HOST_QUALS,
 API_HOST_SVR,
 API_HOST_USERS,
 COMPLAINTS_API_ROOT,
 API_HOST_APQ,
 API_HOST_PE,
 API_HOST_FOI,
 API_HOST_SARS,
 API_HOST_LCM,
} from '../config';
import { UserType } from '../redux/types';
import { LinkedRecordType } from './LinkedRecordType';
import { LinkedRecordTypeDefinition } from './LinkedRecordTypeDefinition';
import { MinimalUser } from './MinimalUser';

const DEFAULT_PARAM_MAPPER = (params: QueryParams) => ({
 ...params,
 // searchDialog wants to set a `query` param, whereas our search
 //  services all want a `?search=` param
 search: params.query,
 query: undefined,
});

const COMPLAINTS_ROLES = [3, 29, 30, 31, 32];
const RISK_ROLES = [3, 12];
const EN_ROLES = [3, 9, 13, 14, 15];
const PE_ROLES = [3, 37];
const FOI_ROLES = [3, 38];
const LCM_ROLES = [3, 39, 40, 41, 42];

const hasRole = (u: MinimalUser, roleSet: number[]) => {
 return u?.roles?.filter((role) => roleSet.includes(role))?.length > 0 ?? false;
};

export const linkedRecordTypeDefinitions: LinkedRecordTypeDefinition[] = [
 {
  type: LinkedRecordType.organisation,
  label: 'Organisation',
  searchUrlRoot: `${API_HOST_ORGS}/organisations?`,
  searchResponseMapper: (item) => ({
   id: item.itemId,
   label: item.name,
   disabled: item.disabled,
  }),
  searchParamMapper: DEFAULT_PARAM_MAPPER,
  navigatePathRoot: '/organisations/',
  allowAccess: (u) =>
   u.userType === UserType.Ofqual || u.userType == UserType.CCEA,
 },
 {
  type: LinkedRecordType.user,
  label: 'User',
  searchUrlRoot: `${API_HOST_USERS}/users?`,
  searchResponseMapper: (item) => ({
   id: item.upn,
   label: item.fullName,
   disabled: item.disabled,
  }),
  searchParamMapper: DEFAULT_PARAM_MAPPER,
  navigatePathRoot: '/users/',
 },
 {
  type: LinkedRecordType.complaint,
  label: 'Complaint',
  searchUrlRoot: `${COMPLAINTS_API_ROOT}/cases?`,
  searchResponseMapper: (item) => ({
   id: item.id,
   label: item.title,
   disabled: item.disabled,
  }),
  searchParamMapper: DEFAULT_PARAM_MAPPER,
  navigatePathRoot: '/complaints/',
  allowAccess: (u) => hasRole(u, COMPLAINTS_ROLES),
 },
 {
  type: LinkedRecordType.contactAo,
  label: 'Contact AO',
  searchUrlRoot: `${API_HOST_COMMS}/informationrequests?status=2`,
  searchResponseMapper: (item) => ({
   id: item.id,
   label: item.subject,
   disabled: item.disabled,
  }),
  searchParamMapper: DEFAULT_PARAM_MAPPER,
  navigatePathRoot: '/communications/contact-ao/',
  allowAccess: (u) => u.userType === UserType.Ofqual,
 },
 {
  type: LinkedRecordType.eventNotification,
  label: 'Event Notification',
  searchUrlRoot: `${API_HOST_COMMS}/eventnotifications?`,
  searchResponseMapper: (item) => ({
   id: item.id,
   label: item.title,
   disabled: item.disabled,
  }),
  searchParamMapper: DEFAULT_PARAM_MAPPER,
  navigatePathRoot: '/communications/event-notifications/',
  allowAccess: (u) => hasRole(u, EN_ROLES),
 },
 {
  type: LinkedRecordType.ctr,
  label: 'CTRs',
  searchUrlRoot: `${API_HOST_COMMS}/ctrs?`,
  searchResponseMapper: (item) => ({
   id: item.id,
   label: item.title,
   disabled: item.disabled,
  }),
  searchParamMapper: DEFAULT_PARAM_MAPPER,
  navigatePathRoot: '/communications/contact-the-regulator/',
 },
 {
  type: LinkedRecordType.risk,
  label: 'Risk',
  searchUrlRoot: `${API_HOST_SVR}/submissions?`,
  searchResponseMapper: (item) => ({
   id: item.id,
   label: item.subject,
   disabled: item.disabled,
  }),
  searchParamMapper: DEFAULT_PARAM_MAPPER,
  navigatePathRoot: '/svr/',
  allowAccess: (u) => hasRole(u, RISK_ROLES),
 },
 {
  type: LinkedRecordType.unit,
  label: 'Unit',
  searchUrlRoot: `${API_HOST_QUALS}/units?`,
  searchResponseMapper: (item) => ({
   id: item.id,
   label: `${item.unitReferenceNumber} - ${item.title}`,
   ref: item.unitReferenceNumber,
   disabled: item.disabled,
  }),
  searchParamMapper: DEFAULT_PARAM_MAPPER,
  navigatePathRoot: '/units/',
  metadataCreator: (item) => ({ URN: item.ref }),
 },
 {
  type: LinkedRecordType.qualification,
  label: 'Qualification',
  searchUrlRoot: `${API_HOST_QUALS}/qualifications?`,
  searchResponseMapper: (item) => ({
   id: item.id,
   label: `${item.qualificationNumber} - ${item.title}`,
   ref: item.qualificationNumber,
   disabled: item.disabled,
  }),
  searchParamMapper: DEFAULT_PARAM_MAPPER,
  navigatePathRoot: '/qualifications/',
  metadataCreator: (item) => ({ QAN: item.ref }),
 },
 {
  type: LinkedRecordType.apq,
  label: 'APQs',
  searchUrlRoot: `${API_HOST_APQ}/apqs?`,
  searchResponseMapper: (item) => ({
   id: item.id,
   label: `${item.queryReferenceNumber} - ${item.queryTitle}`,
   ref: item.queryReferenceNumber,
   disabled: item.disabled,
  }),
  searchParamMapper: DEFAULT_PARAM_MAPPER,
  navigatePathRoot: '/assessment-plan-query/',
  metadataCreator: (item) => ({ Reference: item.ref }),
 },
 {
  type: LinkedRecordType.foi,
  label: 'FOIs',
  searchUrlRoot: `${API_HOST_FOI}/foiItems?`,
  searchResponseMapper: (item) => ({
   id: item.id,
   label: `${item.referenceNumber}: ${item.caseTitle}`,
   disabled: item.disabled,
  }),
  searchParamMapper: DEFAULT_PARAM_MAPPER,
  navigatePathRoot: '/foi/',
  allowAccess: (u) => hasRole(u, FOI_ROLES),
 },
 {
  type: LinkedRecordType.publicEnquiry,
  label: 'Public Enquiries',
  searchUrlRoot: `${API_HOST_PE}/publicenquiries?`,
  searchResponseMapper: (item) => ({
   id: item.id,
   label: `${item.referenceNumber}: ${item.title}`,
   disabled: item.disabled,
  }),
  searchParamMapper: DEFAULT_PARAM_MAPPER,
  navigatePathRoot: '/public-enquiries/list/',
  allowAccess: (u) => hasRole(u, PE_ROLES),
 },
 {
  type: LinkedRecordType.Lcm,
  label: 'Legal cases',
  searchUrlRoot: `${API_HOST_LCM}/lcm?`,
  searchResponseMapper: (item) => ({
   id: item.id,
   label: `${item.referenceNumber}: ${item.caseTitle}`,
   disabled: item.disabled,
  }),
  searchParamMapper: DEFAULT_PARAM_MAPPER,
  navigatePathRoot: '/lcm/',
  allowAccess: (u) => hasRole(u, LCM_ROLES),
 },
 // {
 //   type: LinkedRecordType.sar,
 //   label: "SARs",
 //   searchUrlRoot: `${API_HOST_SARS}/sars?`,
 //   searchResponseMapper: (item) => ({ id: item.id, label: item.title, disabled: item.disabled }),
 //   searchParamMapper: DEFAULT_PARAM_MAPPER,
 //   navigatePathRoot: "/sars/",
 // },
];
